import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RelatedListSidebar, NotesPanel, type RelatedTab } from "@/components/related-list-sidebar";
import { ActivitiesPanel } from "@/components/activities-panel";
import { TagsPanel } from "@/components/tags-panel";
import { Trash2, Mail } from "lucide-react";
import { EmailComposer } from "@/components/email-composer";
import { AttachmentsPanel } from "@/components/attachments-panel";
import { QuickAddTask } from "@/components/quick-add-task";
import { QuickAddMeeting } from "@/components/quick-add-meeting";
import { QuickLogActivity } from "@/components/quick-log-activity";
import { CompetitorsPanel } from "@/components/competitors-panel";
import { AccountHierarchyPanel } from "@/components/hierarchy-tree";

function KV({ k, v }: { k: string; v: React.ReactNode }) {
  if (v === null || v === undefined || v === "" || v === false) return null;
  return (
    <div className="flex justify-between gap-4 border-b py-1.5 last:border-0">
      <span className="text-xs text-muted-foreground">{k}</span>
      <span className="text-sm text-right">{v}</span>
    </div>
  );
}

function Address({ label, street, city, state, zip, country }: {
  label: string; street?: string | null; city?: string | null; state?: string | null; zip?: string | null; country?: string | null;
}) {
  if (!street && !city && !state && !zip && !country) return null;
  const parts = [city, state, zip, country].filter(Boolean).join(", ");
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm">
        {street && <div>{street}</div>}
        {parts && <div>{parts}</div>}
      </div>
    </div>
  );
}

function currency(v: any) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (Number.isNaN(n)) return null;
  return `$${n.toLocaleString()}`;
}

const TABS: RelatedTab[] = ["notes", "activities", "meetings", "tasks", "competitors", "hierarchy", "attachments", "emails", "tags"];

export default function AccountDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState<RelatedTab>("notes");
  const [composerOpen, setComposerOpen] = useState(false);

  const queryKey = ["/accounts", id];
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => (await api.get(`/accounts/${id}`)).data,
    enabled: !!id,
  });

  async function del() {
    if (!data || !confirm(`Delete account "${data.name}"?`)) return;
    await api.delete(`/accounts/${id}`);
    qc.invalidateQueries({ queryKey: ["/accounts"] });
    navigate("/accounts");
  }

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  if (!data) return <div className="p-6 text-sm text-muted-foreground">Not found.</div>;

  const ratingColor = data.rating === "HOT" ? "destructive" : data.rating === "WARM" ? "default" : "secondary";

  return (
    <>
      <PageHeader
        title={data.name}
        description={`${data.type} · ${data.entity}${data.industry ? ` · ${data.industry}` : ""}`}
        actions={
          <>
            <Button variant="outline" onClick={() => setComposerOpen(true)}><Mail className="mr-1 h-4 w-4"/>Send Email</Button>
            <Button variant="outline" onClick={del}><Trash2 className="mr-1 h-4 w-4"/>Delete</Button>
          </>
        }
      />

      <div className="flex flex-1 min-h-[70vh]">
        <RelatedListSidebar
          tabs={TABS}
          active={tab}
          onSelect={setTab}
          counts={{ tags: data.tags?.length } as any}
        />

        <div className="flex-1 overflow-auto p-6 space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader><CardTitle className="text-base">Identity</CardTitle></CardHeader>
              <CardContent>
                <KV k="Legal name" v={data.legalName} />
                <KV k="Type" v={<Badge variant="secondary">{data.type}</Badge>} />
                <KV k="Entity" v={data.entity} />
                <KV k="Rating" v={data.rating ? <Badge variant={ratingColor as any}>{data.rating}</Badge> : null} />
                <KV k="Health score" v={data.healthScore !== null && data.healthScore !== undefined ? `${data.healthScore}/100` : null} />
                <KV k="Owner" v={data.owner?.name} />
                <KV k="Locked" v={data.locked ? "Yes" : null} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Company profile</CardTitle></CardHeader>
              <CardContent>
                <KV k="Industry" v={data.industry} />
                <KV k="Size range" v={data.size} />
                <KV k="Employees" v={data.employeeCount} />
                <KV k="Annual revenue" v={currency(data.annualRevenue)} />
                <KV k="Phone" v={data.phone} />
                <KV k="Website" v={data.website ? <a className="hover:underline" href={data.website} target="_blank" rel="noreferrer">{data.domain ?? data.website}</a> : null} />
                <KV k="LinkedIn" v={data.linkedinUrl ? <a className="hover:underline" href={data.linkedinUrl} target="_blank" rel="noreferrer">Profile</a> : null} />
              </CardContent>
            </Card>

            {(data.billingStreet || data.shippingStreet) && (
              <Card className="md:col-span-2">
                <CardHeader><CardTitle className="text-base">Addresses</CardTitle></CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-2">
                  <Address label="Billing"  street={data.billingStreet}  city={data.billingCity}  state={data.billingState}  zip={data.billingCode}  country={data.billingCountry} />
                  <Address label="Shipping" street={data.shippingStreet} city={data.shippingCity} state={data.shippingState} zip={data.shippingCode} country={data.shippingCountry} />
                </CardContent>
              </Card>
            )}

            <Card className="md:col-span-2">
              <CardHeader><CardTitle className="text-base">Contacts ({data.contacts?.length ?? 0})</CardTitle></CardHeader>
              <CardContent className="space-y-1 text-sm">
                {data.contacts?.length === 0 && <div className="text-muted-foreground">No contacts yet.</div>}
                {data.contacts?.map((c: any) => (
                  <div key={c.id} className="flex justify-between border-b py-1 last:border-0">
                    <Link to={`/contacts/${c.id}`} className="hover:underline">{c.name}</Link>
                    <span className="text-muted-foreground">{c.title ?? ""}</span>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="md:col-span-2">
              <CardHeader><CardTitle className="text-base">Deals ({data.deals?.length ?? 0})</CardTitle></CardHeader>
              <CardContent className="space-y-1 text-sm">
                {data.deals?.length === 0 && <div className="text-muted-foreground">No deals yet.</div>}
                {data.deals?.map((d: any) => (
                  <div key={d.id} className="flex items-center justify-between border-b py-1 last:border-0">
                    <span>{d.name}</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">{d.stage}</Badge>
                      <span className="tabular-nums">${Number(d.valueUsd).toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader><CardTitle className="text-base capitalize">{tab}</CardTitle></CardHeader>
            <CardContent>
              {tab === "notes" && <NotesPanel entityType="Account" entityId={data.id} />}
              {tab === "activities" && (
                <div className="space-y-3">
                  <QuickLogActivity
                    accountId={data.id}
                    invalidateQueryKeys={[["/activities", { field: "accountId", id: data.id }]]}
                  />
                  <ActivitiesPanel field="accountId" id={data.id} />
                </div>
              )}
              {tab === "meetings" && (
                <div className="space-y-3">
                  <QuickAddMeeting
                    accountId={data.id}
                    invalidateQueryKeys={[["/activities", { field: "accountId", id: data.id, type: "MEETING" }]]}
                  />
                  <ActivitiesPanel field="accountId" id={data.id} type="MEETING" emptyMessage="No meetings yet." />
                </div>
              )}
              {tab === "tasks" && (
                <div className="space-y-3">
                  <QuickAddTask
                    accountId={data.id}
                    invalidateQueryKeys={[["/activities", { field: "accountId", id: data.id, type: "TASK" }]]}
                  />
                  <ActivitiesPanel field="accountId" id={data.id} type="TASK" emptyMessage="No tasks yet." />
                </div>
              )}
              {tab === "emails" && (
                <div className="space-y-3">
                  <QuickLogActivity
                    accountId={data.id}
                    kinds={["EMAIL"]}
                    submitLabel="Log email"
                    invalidateQueryKeys={[["/activities", { field: "accountId", id: data.id, type: "EMAIL" }]]}
                  />
                  <ActivitiesPanel field="accountId" id={data.id} type="EMAIL" emptyMessage="No email history yet. Log emails you sent from Outlook via the button above, or wait for M365 sync." />
                </div>
              )}
              {tab === "attachments" && <AttachmentsPanel entityType="Account" entityId={data.id} />}
              {tab === "competitors" && <CompetitorsPanel endpoint={`/accounts/${data.id}/competitors`} scope="Account" />}
              {tab === "hierarchy"   && <AccountHierarchyPanel accountId={data.id} />}
              {tab === "tags"        && <TagsPanel endpoint={`/accounts/${data.id}`} queryKey={queryKey} tags={data.tags ?? []} />}
            </CardContent>
          </Card>
        </div>
      </div>

      <EmailComposer
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        contact={data.contacts?.[0]}
        account={data}
        invalidateQueryKeys={[["/activities", { field: "accountId", id: data.id }]]}
      />
    </>
  );
}
